import React, { useState, useEffect } from "react";
import { Card, Grid, Icon, TextField, Autocomplete, Box } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import {
  autoCompleteProps,
  selectSx,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { useDispatch, useSelector } from "react-redux";
import { getProductList } from "actions/product";
import { getProjectList } from "actions/project";
import { getUnitListing } from "actions/unit";
import { getStationList } from "actions/station";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_RECORDS_PER_PAGE,
} from "components/common/constant";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  getPlanningReportList,
  exportPlanningReportData,
} from "actions/reports";
import { planningReportList } from "constants/reports";
import { getDepartmentList } from "actions/department";

function DepartmentWorkloadListing() {
  const dispatch = useDispatch();
  const { notify } = useNotify();

  const [exportLoading, setExportLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const today = dayjs().format("YYYY-MM-DD");

  const [filters, setFilters] = useState({
    dateRange: "TODAY",
    projectNo: "",
    typeOfProduct: "",
    unitID: null,
    department: "",
    station: "",

    startDate: today,
    endDate: today,

    filterParams: JSON.stringify([
      {
        field: "Date",
        condition: "gte",
        filterValues: [today],
      },
      {
        field: "Date",
        condition: "lte",
        filterValues: [today],
      },
    ]),
  });

  const [showClearButton, setShowClearButton] = useState(false);

  const stationListData = useSelector((state) => state.station.stationList);

  const productListData = useSelector((state) => state.product.productList);

  const projectListData = useSelector((state) => state.project.projectList);

  const unitListingData = useSelector((state) => state.unit.unitList);

  const reportListData = useSelector(
    (state) => state.employeeActivityReport?.planningReportList,
  );

  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  useEffect(() => {
    getPlanningReportList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({
        type: planningReportList,
        payload: {},
      });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (reportListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: reportListData?.data?.planningReport?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (reportListData?.code || reportListData?.message) {
      setListState({
        isLoading: false,
        error:
          reportListData?.message ||
          "Failed to load department workload report!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [reportListData]);

  useEffect(() => {
    getProductList(dispatch);
    getProjectList(dispatch);
    getUnitListing(dispatch);
    getStationList(dispatch);
    getDepartmentList(dispatch);
  }, [dispatch]);

  const getDateRange = (days) => {
    const today = new Date();

    if (days === "TODAY") {
      const todayString = dayjs(today).format("YYYY-MM-DD");

      return {
        startDate: todayString,
        endDate: todayString,
      };
    }

    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - 1);

    if (days === "1") {
      const yesterdayString = dayjs(endDate).format("YYYY-MM-DD");

      return {
        startDate: yesterdayString,
        endDate: yesterdayString,
      };
    }

    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (Number(days) - 1));

    return {
      startDate: dayjs(startDate).format("YYYY-MM-DD"),
      endDate: dayjs(endDate).format("YYYY-MM-DD"),
    };
  };

  const buildFilterParams = () => {
    const filterParams = [];

    filterParams.push({
      field: "Date",
      condition: "gte",
      filterValues: [filters.startDate],
    });

    filterParams.push({
      field: "Date",
      condition: "lte",
      filterValues: [filters.endDate],
    });

    if (filters.typeOfProduct) {
      filterParams.push({
        field: "ProductType",
        condition: "like",
        filterValues: [filters.typeOfProduct],
      });
    }

    if (filters.projectNo) {
      filterParams.push({
        field: "ProjectNo",
        condition: "like",
        filterValues: [filters.projectNo],
      });
    }

    if (filters.unitID) {
      filterParams.push({
        field: "UnitNo",
        condition: "eq",
        filterValues: [String(filters.unitID)],
      });
    }

    if (filters.department) {
      filterParams.push({
        field: "DepartmentName",
        condition: "like",
        filterValues: [filters.department],
      });
    }

    if (filters.station) {
      filterParams.push({
        field: "StationName",
        condition: "like",
        filterValues: [filters.station],
      });
    }

    return JSON.stringify(filterParams);
  };

  const unitOptions =
    unitListingData?.data?.Unit?.map((item) => ({
      id: item.ID,
      name: item.unitNo,
    })) || [];

  const projectOptions =
    projectListData?.data?.project?.map((item) => ({
      id: item.ID,
      projectNo: item.projectNo,
    })) || [];

  const productOptions =
    productListData?.data?.Product?.map((item) => ({
      id: item.ID,
      name: item.type,
    })) || [];

  const data =
    reportListData?.data?.planningReport?.map((item, index) => ({
      srNo: index + 1,
      customerName: item.customerName || "-",
      rating: item.rating || "-",
      productType: item.productType || "-",
      projectNo: item.projectNo || "-",
      unitNo: item.unitNo || "-",
      subUnit: item.subUnitNo || "-",
      department: item.departmentName || "-",
      station: item.stationName || "-",
      techInvolve: item.noOfTechInvolve || 0,
      directActivities: item.noOfDirectActivities || 0,
      totalHoursBooked: item.totalHoursBooked || 0,
    })) || [];

  const handleSearch = () => {
    const filterParams = buildFilterParams();

    if (filterParams === filters.filterParams) {
      return;
    }

    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams,
    }));

    setShowClearButton(true);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const handleClear = () => {
    const today = dayjs().format("YYYY-MM-DD");

    setFilters({
      dateRange: "TODAY",
      projectNo: "",
      typeOfProduct: "",
      unitID: null,
      department: "",
      station: "",

      startDate: today,
      endDate: today,

      filterParams: JSON.stringify([
        {
          field: "Date",
          condition: "gte",
          filterValues: [today],
        },
        {
          field: "Date",
          condition: "lte",
          filterValues: [today],
        },
      ]),
    });

    setShowClearButton(false);

    if (pageNum !== 1) {
      setPageNum(1);
    }
  };

  const columns = [
    {
      Header: "Sr No",
      accessor: "srNo",
      width: "115px",
    },
    {
      Header: "Customer Name",
      accessor: "customerName",
      width: "180px",
      Cell: (cell) => wrapCell(cell.value || "-", "180px"),
    },
    {
      Header: "Rating",
      accessor: "rating",
      width: "100px",
      Cell: (cell) => wrapCell(cell.value || "-", "100px"),
    },
    {
      Header: "Product Type",
      accessor: "productType",
      width: "140px",
      Cell: (cell) => wrapCell(cell.value || "-", "140px"),
    },
    {
      Header: "Project No",
      accessor: "projectNo",
      width: "140px",
      Cell: (cell) => wrapCell(cell.value || "-", "140px"),
    },
    {
      Header: "Unit No",
      accessor: "unitNo",
      width: "120px",
      Cell: (cell) => wrapCell(cell.value || "-", "120px"),
    },
    {
      Header: "Sub Unit",
      accessor: "subUnit",
      width: "180px",
      Cell: (cell) => wrapCell(cell.value || "-", "180px"),
    },
    {
      Header: "Department",
      accessor: "department",
      width: "165px",
      Cell: (cell) => wrapCell(cell.value || "-", "165px"),
    },
    {
      Header: "Station",
      accessor: "station",
      width: "150px",
      Cell: (cell) => wrapCell(cell.value || "-", "150px"),
    },
    {
      Header: "No of Tech Involve",
      accessor: "techInvolve",
      width: "170px",
      Cell: (cell) => wrapCell(cell.value || "-", "170px"),
    },
    {
      Header: "No of Direct Activities",
      accessor: "directActivities",
      width: "190px",
      Cell: (cell) => wrapCell(cell.value || "-", "190px"),
    },
    {
      Header: "Total Hours Booked",
      accessor: "totalHoursBooked",
      width: "170px",
      Cell: (cell) => wrapCell(cell.value || "-", "170px"),
    },
  ];

  const downloadPlanningReportCSV = async () => {
    try {
      setExportLoading(true);

      const res = await exportPlanningReportData(filters.filterParams);
      
      const url = window.URL.createObjectURL(res.data);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "PlanningReport.csv");

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      notify(
        res?.message || "PlanningReport file downloaded successfully",
        "success",
      );
    } catch (error) {
      notify("Failed to download planningReport file", "error");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* Header */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Department Workload Report
                </MDTypography>
                <MDBox display="flex" gap={2}>
                  <MDButton
                    variant="gradient"
                    color="light"
                    startIcon={<Icon>file_upload</Icon>}
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                    onClick={downloadPlanningReportCSV}
                  >
                    {exportLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin mr-2" />
                        Exporting...
                      </>
                    ) : (
                      "Export Report"
                    )}
                  </MDButton>
                </MDBox>
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={DATE_RANGE_OPTIONS}
                      value={
                        DATE_RANGE_OPTIONS.find(
                          (item) => item.value === filters.dateRange,
                        ) || null
                      }
                      getOptionLabel={(option) => option.label}
                      onChange={(event, value) => {
                        if (!value) return;

                        if (value.value === "CUSTOM") {
                          setFilters({
                            ...filters,
                            dateRange: "CUSTOM",
                          });
                        } else {
                          const dates = getDateRange(value.value);

                          setFilters({
                            ...filters,
                            dateRange: value.value,
                            startDate: dates.startDate,
                            endDate: dates.endDate,
                          });
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Date Range"
                        />
                      )}
                    />
                  </Grid>
                  {filters.dateRange === "CUSTOM" && (
                    <>
                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            value={dayjs(filters.startDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                startDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4} lg={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            disableFuture
                            label="End Date"
                            minDate={dayjs(filters.startDate)}
                            value={dayjs(filters.endDate)}
                            onChange={(value) =>
                              setFilters((prev) => ({
                                ...prev,
                                endDate: value?.format("YYYY-MM-DD") || "",
                              }))
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentListData?.data?.Department || []}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        departmentListData?.data?.Department?.find(
                          (item) => item.name === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={productOptions}
                      value={
                        productOptions.find(
                          (item) => item.name === filters.typeOfProduct,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          typeOfProduct: value?.name || null,
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Product Type"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={projectOptions}
                      value={
                        projectOptions.find(
                          (item) => item.projectNo === filters.projectNo,
                        ) || null
                      }
                      getOptionLabel={(option) => option.projectNo || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          projectNo: value?.projectNo || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value?.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select Project No"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={unitOptions}
                      value={
                        unitOptions.find(
                          (item) => item.id === filters.unitID,
                        ) || null
                      }
                      getOptionLabel={(option) => option.name || ""}
                      onChange={(event, value) =>
                        setFilters({
                          ...filters,
                          unitID: value?.id || null,
                        })
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select Unit" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal={true}
                      size="small"
                      options={stationListData?.data?.station || []}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        stationListData?.data?.station?.find(
                          (stat) => stat.name === filters.station,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          station: newValue?.name || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select station" />
                      )}
                    />
                  </Grid>

                  {/* Search */}
                  <Grid item xs={12} sm={6} md={4} lg="auto">
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {showClearButton && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Total Records */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue">
                  Total Records: {reportListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              {/* Table */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No records found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={data || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (reportListData?.data?.totalRecords || 0) /
                        (reportListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default DepartmentWorkloadListing;
